// inv/src/pages/FAQ.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BookOpenText,
  ChevronDown,
  CircleHelp,
  FileText,
  Mail,
  Search,
  X,
} from "lucide-react";
import { useInitialData } from "../components/data/DataContext";
import SeoHead from "../seo/seoHead";
import { API_URL } from "../config";

const ARTICLE_LIMIT = 3;

const parseDate = (value) => {
  if (!value) return null;
  let raw = value;
  if (typeof value === "object" && value && "$date" in value) {
    const v = value.$date;
    raw = typeof v === "object" && v && "$numberLong" in v ? Number(v.$numberLong) : v;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value, locale) => {
  const date = parseDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(locale || "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

function FAQAccordion({ items }) {
  const [openItem, setOpenItem] = useState(items?.[0]?.id || "");

  useEffect(() => {
    setOpenItem(items?.[0]?.id || "");
  }, [items]);

  if (!items?.length) return null;

  return (
    <div className="divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white shadow-sm">
      {items.map((item) => {
        const isOpen = openItem === item.id;

        return (
          <div key={item.id}>
            <button
              type="button"
              onClick={() => setOpenItem(isOpen ? "" : item.id)}
              className="flex w-full cursor-pointer items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-stone-50"
              aria-expanded={isOpen}
            >
              <span className="text-sm font-semibold leading-6 text-stone-950">
                {item.question}
              </span>
              <ChevronDown
                className={`mt-1 h-4 w-4 shrink-0 text-stone-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isOpen ? (
              <div className="px-5 pb-5 text-sm leading-6 text-stone-600">
                {item.answer}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ArticleCard({ article, languagePrefix, locale }) {
  const dateText = formatDate(article.publish_date, locale);

  return (
    <Link
      to={`${languagePrefix}/blog/${article.url_slug}/`}
      className="group block rounded-lg border border-stone-200 bg-white p-4 shadow-sm transition-colors hover:border-stone-300 hover:bg-stone-50"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-stone-100 text-stone-700">
          <FileText className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-5 text-stone-950 group-hover:underline">
            {article.title}
          </h3>
          {article.short_description ? (
            <p className="mt-1 line-clamp-2 text-xs leading-5 text-stone-600">
              {article.short_description}
            </p>
          ) : null}
          {dateText ? (
            <p className="mt-2 text-xs font-medium text-stone-500">{dateText}</p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export default function FAQPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { seo = {} } = useInitialData();
  const [activeSectionId, setActiveSectionId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [articles, setArticles] = useState([]);

  const {
    title,
    description,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType = "website",
    twitterCard = "summary_large_image",
  } = seo;

  const languagePrefix = i18n.language === "en" ? "" : `/${i18n.language}`;
  const sections = t("pageFAQ.sections", { returnObjects: true });
  const links = t("pageFAQ.quickLinks", { returnObjects: true });
  const faqSections = Array.isArray(sections) ? sections : [];
  const quickLinks = Array.isArray(links) ? links : [];
  const activeSection = faqSections.find((section) => section.id === activeSectionId) || faqSections[0];
  const normalizedQuery = searchQuery.trim().toLowerCase();

  useEffect(() => {
    if (!activeSectionId && faqSections.length) {
      setActiveSectionId(faqSections[0].id);
    }
  }, [activeSectionId, faqSections]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (!hash || !faqSections.length) return;

    const section = faqSections.find((item) => item.id === hash);
    if (section) {
      setActiveSectionId(section.id);
      setSearchQuery("");
      window.requestAnimationFrame(() => {
        document.getElementById(section.id)?.scrollIntoView({ block: "start" });
      });
    }
  }, [faqSections, location.hash]);

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_URL}/articles-cdn?limit=${ARTICLE_LIMIT}`)
      .then((res) => (res.ok ? res.json() : { articles: [] }))
      .then((data) => {
        if (!cancelled) setArticles(Array.isArray(data?.articles) ? data.articles : []);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleSections = useMemo(() => {
    if (!normalizedQuery) {
      return activeSection ? [activeSection] : [];
    }

    return faqSections
      .map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => {
          const text = `${item.question || ""} ${item.answer || ""}`.toLowerCase();
          return text.includes(normalizedQuery);
        }),
      }))
      .filter((section) => section.items.length);
  }, [activeSection, faqSections, normalizedQuery]);

  const totalQuestions = faqSections.reduce((sum, section) => sum + (section.items?.length || 0), 0);

  return (
    <>
      <SeoHead
        lang={i18n.language}
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImage}
        ogUrl={ogUrl}
        ogType={ogType}
        twitterCard={twitterCard}
        alternates={seo.alternates}
        jsonLd={seo.jsonLd}
      />

      <div className="bg-stone-50/40">
        <section className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-medium text-stone-600 shadow-sm">
                <CircleHelp className="h-3.5 w-3.5" />
                {t("pageFAQ.badge")}
              </div>
              <h1 className="mt-4 text-4xl font-bold tracking-tight text-stone-950 md:text-5xl">
                {t("pageFAQ.title")}
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-7 text-stone-600">
                {t("pageFAQ.description")}{" "}
                <Link to={`${languagePrefix}/pricing-policy`} className="font-medium text-blue-600 hover:underline">
                  {t("pageFAQ.pricingLink")}
                </Link>
                <span>.</span>
              </p>

              <div className="mt-6 max-w-2xl">
                <label htmlFor="faq-search" className="sr-only">
                  {t("pageFAQ.searchLabel")}
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                  <input
                    id="faq-search"
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t("pageFAQ.searchPlaceholder")}
                    className="h-11 w-full rounded-lg border border-stone-200 bg-white pl-10 pr-10 text-sm text-stone-900 shadow-sm outline-none transition-colors placeholder:text-stone-400 focus:border-stone-300 focus:ring-4 focus:ring-stone-100"
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      aria-label={t("pageFAQ.clearSearch")}
                      className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <aside className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm lg:self-end">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-stone-100 text-stone-700">
                  <BookOpenText className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-2xl font-semibold tracking-tight text-stone-950">
                    {totalQuestions}
                  </div>
                  <div className="text-xs font-medium text-stone-500">
                    {t("pageFAQ.totalQuestions")}
                  </div>
                </div>
              </div>
              <div className="mt-4 border-t border-stone-200 pt-4">
                <p className="text-sm leading-6 text-stone-600">{t("pageFAQ.supportText")}</p>
                <a
                  href="mailto:contact@crino.io"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-stone-950 hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  {t("pageFAQ.contactSupport")}
                </a>
              </div>
            </aside>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-6 px-6 pb-16 lg:grid-cols-[260px_minmax(0,1fr)_320px] lg:px-8">
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
              <div className="px-2 pb-2 text-xs font-semibold uppercase text-stone-500">
                {t("pageFAQ.sectionsTitle")}
              </div>
              <div className="space-y-1">
                {faqSections.map((section) => {
                  const isActive = section.id === activeSection?.id && !normalizedQuery;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => {
                        setActiveSectionId(section.id);
                        setSearchQuery("");
                      }}
                      className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? "bg-stone-900 font-medium text-white"
                          : "text-stone-600 hover:bg-stone-100 hover:text-stone-950"
                      }`}
                    >
                      <span>{section.title}</span>
                      <span className={isActive ? "text-stone-300" : "text-stone-400"}>
                        {section.items?.length || 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <div className="min-w-0 space-y-6">
            {visibleSections.length ? (
              visibleSections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-8">
                  <div className="mb-4">
                    <h2 className="text-2xl font-semibold tracking-tight text-stone-950">
                      {section.title}
                    </h2>
                    {section.description ? (
                      <p className="mt-2 text-sm leading-6 text-stone-600">{section.description}</p>
                    ) : null}
                  </div>
                  <FAQAccordion items={section.items || []} />
                </section>
              ))
            ) : (
              <div className="rounded-lg border border-dashed border-stone-300 bg-white p-6 text-sm leading-6 text-stone-600">
                {t("pageFAQ.noResults")}
              </div>
            )}
          </div>

          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-stone-950">{t("pageFAQ.quickLinksTitle")}</h2>
              <div className="mt-4 space-y-2">
                {quickLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={`${languagePrefix}${link.href}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50/70 px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-white hover:text-stone-950"
                  >
                    <span>{link.label}</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ))}
              </div>
            </div>

            {articles.length ? (
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold text-stone-950">{t("pageFAQ.resourcesTitle")}</h2>
                  <Link
                    to={`${languagePrefix}/blog/`}
                    className="text-xs font-medium text-stone-500 hover:text-stone-950 hover:underline"
                  >
                    {t("pageFAQ.viewAllArticles")}
                  </Link>
                </div>
                <div className="mt-4 space-y-3">
                  {articles.map((article) => (
                    <ArticleCard
                      key={article._id || article.url_slug}
                      article={article}
                      languagePrefix={languagePrefix}
                      locale={i18n.language}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </aside>
        </section>
      </div>
    </>
  );
}
